import { IApiManagement } from "@kuika/kuika-cl-model";
import * as React from "react";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import { KuikaNameModal } from "../../ui-kit/kuika-name-modal/kuika-name-modal";

/**
 * Asks for the new API's name, and is the ONE place the API-name rule lives.
 *
 * Before this there were two and they disagreed: the create hero accepted
 * `^[a-zA-Z0-9 ]+$` while the editor's rename required a leading letter, so an API created
 * as "24h Sync" could never be renamed afterwards. The stricter rule wins rather than the
 * looser one — unusually, because this name is not just a label. `ApiGenerator` emits each
 * API as `Backend/Apis/{name}.cs` and uses `name.RemoveSpaces()` as the class AND as a
 * namespace segment, so a digit-leading name produces C# that will not compile. Accepting
 * one was a latent codegen fault, not a capability.
 */

/** Start with a letter; letters, digits and spaces after that. Minimum two characters. */
export const API_NAME_PATTERN: RegExp = /^[A-Za-z][A-Za-z0-9 ]+$/;
export const API_NAME_MESSAGE: string =
  "API name must start with a letter and can only contain letters, numbers and spaces.";

/** The confirm says what it is doing in its own words: an API is created, not saved. */
const CREATE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Creating", success: "Created" };

export interface IApiNameModalProps {
  visible: boolean;
  title: string;
  subtitle?: string;
  confirmLabel: string;
  /**
   * Where the create stands. The ✓ is held on the confirm before the new API's editor replaces
   * the page, and a failed create leaves the sheet open on "Try again".
   */
  status: KuikaButtonStatus;
  /** Every API in the project — the uniqueness check runs against all of them. */
  apis: IApiManagement[];
  /** Reserved platform keys (`project.commonReservedKeys`). */
  reservedKeys: string[];
  onCancel: () => void;
  onSubmit: (name: string) => void;
}

export class ApiNameModal extends React.PureComponent<IApiNameModalProps> {
  /**
   * Case-insensitive, and deliberately NOT locale-aware: `toLowerCase` is kept exactly as
   * the pre-refactor validator had it, so which duplicates get rejected does not shift for
   * Turkish-named projects.
   */
  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const apis: IApiManagement[] = this.props.apis ?? [];
    if (apis.some((api: IApiManagement): boolean => api.name?.toLowerCase() === value.toLowerCase())) {
      throw new Error("This API Name is used before.");
    }
  };

  /**
   * An API named like a platform-reserved key (e.g. "Kuika") collides with the generated
   * root namespace and breaks code generation, so it is rejected before it can be created.
   */
  reservedNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    if (!value) return;
    const reserved: string[] = (this.props.reservedKeys ?? []).map((key: string): string => key.toLowerCase());
    if (reserved.includes(value.toLowerCase())) {
      throw new Error(`"${value}" is a reserved name. Please use another name.`);
    }
  };

  render() {
    return (
      <KuikaNameModal
        visible={this.props.visible}
        title={this.props.title}
        subtitle={this.props.subtitle}
        // One API drawn as a cell cluster, the same mark the list and the editor header
        // carry. `common/api.svg` is a crosshair-ish mark that reads as nothing at 24px.
        iconName="api-service"
        confirmLabel={this.props.confirmLabel}
        status={this.props.status}
        statusLabels={CREATE_STATUS_LABELS}
        fieldName="apiName"
        placeholder="Enter API name"
        rules={[
          { required: true, message: "Please enter a name" },
          { pattern: API_NAME_PATTERN, message: API_NAME_MESSAGE },
          { validator: this.existingNameValidator },
          { validator: this.reservedNameValidator }
        ]}
        // The ids the create form has always emitted, so the E2E suite still finds them.
        inputTestId="apiBuilderNameInput"
        confirmTestId="apiBuilderCreateButton"
        className="kuika-api-name-modal"
        onCancel={this.props.onCancel}
        onSubmit={this.props.onSubmit}
      />
    );
  }
}
